/**
 * Un solo modo di aprire una chat WhatsApp da fuori del drawer.
 *
 * Prima ogni punto faceva a modo suo: il kanban apriva il drawer sul numero
 * giusto (con la scelta del numero aziendale se l'utente ne vede piu' d'uno),
 * la scheda lead usava un pannello incorporato rotto, tabella e intestazione
 * navigavano a una rotta che apriva il drawer vuoto e buttava via il numero.
 *
 * Qui: numero normalizzato, scelta del numero aziendale quando serve, e il
 * drawer si apre sul posto senza cambiare pagina. `canWhatsApp` e' falso anche
 * finche' i numeri sono in caricamento: meglio un pulsante che compare un
 * attimo dopo che uno che apre sul numero sbagliato senza chiedere.
 */
import React, { useCallback, useRef, useState } from 'react';
import { useWhatsAppConfigs } from '../../lib/hooks/useWhatsAppConfigs';
import { useWhatsAppDrawerStore } from '../../lib/stores/whatsappDrawerStore';
import { normalizeWhatsAppDigits } from '../../lib/services/whatsapp/phoneDigits';
import { WhatsAppConfigChooser, type ChooserPos } from './WhatsAppConfigChooser';

const CHOOSER_MENU_WIDTH = 256; // w-64 del menu

export function useOpenWhatsAppChat() {
  const { configs, loading } = useWhatsAppConfigs();
  // Ref per le closure stabili (le card memoizzate non confrontano i callback).
  const configsRef = useRef(configs);
  configsRef.current = configs;
  const [chooser, setChooser] = useState<ChooserPos | null>(null);

  /**
   * Apre il drawer sulla chat col numero dato. Con un numero aziendale solo, o
   * con `configId` noto (es. dalla cronologia), va dritto; con piu' numeri
   * chiede quale usare, ancorato al pulsante premuto. `name` e' il nome da dare
   * alla conversazione se col numero non si e' mai parlato.
   */
  const openChat = useCallback((phone: string, anchor?: DOMRect | null, configId?: string | null, name?: string | null) => {
    const digits = normalizeWhatsAppDigits(phone);
    if (!digits) return;
    const cfgs = configsRef.current;
    if (configId || cfgs.length <= 1 || !anchor) {
      useWhatsAppDrawerStore.getState().openWithChat(digits, configId ?? (cfgs.length === 1 ? cfgs[0].id : null), name ?? null);
      return;
    }
    const menuHeight = 36 + cfgs.length * 34;
    const spaceBelow = window.innerHeight - anchor.bottom;
    setChooser({
      digits,
      name: name ?? null,
      left: Math.max(8, Math.min(anchor.left, window.innerWidth - CHOOSER_MENU_WIDTH - 8)),
      ...(spaceBelow < menuHeight ? { bottom: window.innerHeight - anchor.top + 4 } : { top: anchor.bottom + 4 }),
    });
  }, []);

  const chiudi = useCallback(() => setChooser(null), []);

  const chooserNode = chooser ? (
    <WhatsAppConfigChooser
      pos={chooser}
      configs={configs}
      onClose={chiudi}
      onPick={(cfg) => {
        useWhatsAppDrawerStore.getState().openWithChat(chooser.digits, cfg.id, chooser.name);
        setChooser(null);
      }}
    />
  ) : null;

  return { canWhatsApp: !loading && configs.length > 0, loading, configs, openChat, chooser: chooserNode };
}
